import { isNil, orderBy } from 'lodash'
import { DateTime } from 'luxon'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import { Button, Text, Select, Separator, Alert, Loader } from '@/ui'
import { TypographyStyles } from '@/styles'

import { BalanceSheetBreadcrumbs } from './components/BalanceSheetBreadcrumbs'

import { PdfWrapper, PdfWrapperHandle } from '@/components/PdfWrapper'
import { adminFetchSingleUser } from '@/actions/admin/adminAllUsersActions'
import {
  AdminBalanceSheetRequestPayload,
  BalanceSheetReportData,
  BalanceSheetDropdownData,
  fetchAdminBalanceSheetDropdownOptions,
  generateAdminBalanceSheet,
} from '@/actions/reportActions'
import {
  getUserById,
  selectPrimaryMembershipForUser,
} from '@/selectors/user.selectors'

import { NeedsManualBalanceSheetLabel } from './ManualBalanceSheetLabel'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import BalanceSheetReport from '@/features/reports/Admin/AdminBalanceSheet/components/BalanceSheetReport'

export const AdminBalanceSheetPanel: React.FC = () => {
  const dispatch = useAppDispatch()
  const params = useParams<{ userId: string }>()
  const user = useReselector(getUserById, Number(params.userId))
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  const [dropdownLoading, setDropdownLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [dropdownData, setDropdownData] = useState<
    BalanceSheetDropdownData | undefined
  >()
  const [dropdownSelectedIndex, setDropdownSelectedIndex] = useState<
    number | undefined
  >()
  const [reportData, setReportData] = useState<
    BalanceSheetReportData | undefined
  >()
  const pdfRef = useRef<PdfWrapperHandle>(null)
  const membership = useReselector(selectPrimaryMembershipForUser, user?.id)

  // Invoked upon load, bootstraps data
  useEffect(() => {
    async function performAsync(userId: number) {
      setDropdownLoading(true)
      const dropdownResponse =
        await fetchAdminBalanceSheetDropdownOptions(userId)
      if (dropdownResponse.error) {
        setError(dropdownResponse.error)
      } else if (dropdownResponse.data) {
        setDropdownData(dropdownResponse.data)
      }
      setDropdownLoading(false)
    }
    if (params.userId) {
      const userId = Number(params.userId)
      dispatch(adminFetchSingleUser(userId))
      performAsync(userId)
    }
  }, [dispatch, params.userId])

  const dropdownDataWithEOY = useMemo(() => {
    if (!dropdownData || !currentTaxYear) {
      return null
    }

    const withEOY = { ...dropdownData }
    const eoyKey = `${currentTaxYear}-12`
    if (withEOY[eoyKey]) {
      withEOY[eoyKey] = {
        ...withEOY[eoyKey],
        mostRecentReconciliationEndingBalanceDate: `${currentTaxYear}-12-31T23:59:59.999Z`,
      }
    } else {
      withEOY[eoyKey] = {
        mostRecentReconciliationEndingBalanceDate: `${currentTaxYear}-12-31T23:59:59.999Z`,
        mostRecentReconciliationId: -1,
        financialAccounts: [],
      }
    }
    return withEOY
  }, [dropdownData, currentTaxYear])

  const dropdownOptions = React.useMemo(() => {
    if (!dropdownDataWithEOY) {
      return []
    }

    const sorted = orderBy(
      dropdownDataWithEOY,
      ['mostRecentReconciliationEndingBalanceDate'],
      ['desc']
    )

    return Object.values(sorted).map((item, index) => ({
      text: isoToUTCDateTime(
        item.mostRecentReconciliationEndingBalanceDate
      ).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT),
      key: item.mostRecentReconciliationId,
      value: index.toString(),
    }))
  }, [dropdownDataWithEOY])

  const selectedOption = !isNil(dropdownSelectedIndex)
    ? dropdownOptions[dropdownSelectedIndex]
    : null
  const selectedDateDetails =
    isNil(dropdownDataWithEOY) || isNil(selectedOption)
      ? null
      : Object.values(dropdownDataWithEOY).find(
          (a) => a.mostRecentReconciliationId === selectedOption?.key
        )

  // Invoked when the dropdown value changes, generates balance sheet report data
  useEffect(() => {
    async function performAsync(data: AdminBalanceSheetRequestPayload) {
      setTableLoading(true)
      const response = await generateAdminBalanceSheet(data)
      if (response.error) {
        setError(response.error)
      } else if (response.data) {
        setReportData(response.data)
      }
      setTableLoading(false)
    }

    if (!user || !selectedDateDetails) {
      return
    }

    setError(undefined)
    const requestData: AdminBalanceSheetRequestPayload = {
      userId: user.id,
      mostRecentReconciliationEndingBalanceDate:
        selectedDateDetails.mostRecentReconciliationEndingBalanceDate,
    }

    performAsync(requestData)
  }, [dropdownSelectedIndex, user, selectedDateDetails])

  const selectedDateFormatted = useMemo(
    () =>
      selectedDateDetails
        ? isoToUTCDateTime(
            selectedDateDetails.mostRecentReconciliationEndingBalanceDate
          ).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
        : 'N/A',
    [selectedDateDetails]
  )

  const pdfFileName = useMemo(() => {
    const customerOrBusinessName =
      user?.financialProfile?.businessName ||
      `${user?.firstName} ${user?.lastName}`
    return `${customerOrBusinessName}_${selectedDateFormatted}_balance_sheet.pdf`
  }, [selectedDateFormatted, user])

  const downloadPdf = () => {
    pdfRef.current?.generatePDF()
  }

  const hasReconciliations = dropdownOptions.length > 0

  const DATE_EXPLAINER = `The date dropdown is populated using reconciliation data, specifically the most-recent 'ending balance date' across all accounts.
    Pulling balance sheets for any other dates will require manual calculation.`

  return (
    <div className="w-full flex flex-col gap-4">
      <BalanceSheetBreadcrumbs
        firstName={user?.firstName}
        lastName={user?.lastName}
        userId={user?.id}
      />
      <Separator />

      <div className="flex flex-col gap-4 w-full">
        {/* Header */}
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between">
            <Text variant={TypographyStyles.H1} className="pb-2 w-2/3">
              Balance Sheet
            </Text>
            {!dropdownLoading && hasReconciliations && (
              <div className="flex flex-wrap w-1/3">
                <div className="flex-none w-full">
                  <Select
                    value={
                      dropdownSelectedIndex
                        ? dropdownSelectedIndex.toString()
                        : undefined
                    }
                    placeholder="Please select a date"
                    options={dropdownOptions}
                    onValueChange={(val) => {
                      setDropdownSelectedIndex(Number(val))
                    }}
                  />
                  <br />
                </div>
                {selectedDateDetails && (
                  <Button
                    content="Download as PDF"
                    onClick={downloadPdf}
                    variant="primary"
                    className="flex-none w-full mt-2 text-right"
                  >
                    Download as PDF
                  </Button>
                )}
              </div>
            )}
          </div>
          <Text variant={TypographyStyles.BODY_MD} className="w-full">
            <b>Effective Date: </b>
            {user?.financialProfile?.entityChangeElectionDate
              ? DateTime.fromISO(
                  user?.financialProfile?.entityChangeElectionDate
                )
                  .toUTC()
                  .startOf('day')
                  .toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY)
              : 'None Added'}
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            <b>User joined Heard on: </b>
            {membership?.startDate
              ? DateTime.fromISO(membership?.startDate)
                  .toUTC()
                  .startOf('day')
                  .toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY)
              : 'None Found'}
          </Text>
          {user?.id && (
            <NeedsManualBalanceSheetLabel
              userId={user.id}
              year={currentTaxYear}
            />
          )}

          {dropdownLoading && (
            <div className="flex-none">
              <Loader className="float-right" active />
            </div>
          )}
        </div>
        <Separator />

        {/* Message banner */}
        {!tableLoading && !error && hasReconciliations && (
          <div className="w-full">
            {!dropdownLoading && !selectedDateDetails && (
              <Alert
                title={'Get started by selecting a date above'}
                description={DATE_EXPLAINER}
                className="w-full"
              />
            )}
          </div>
        )}

        {/* Report Table */}
        {reportData && selectedDateDetails && (
          <PdfWrapper filename={pdfFileName} ref={pdfRef}>
            <BalanceSheetReport
              balanceSheetDate={selectedDateFormatted}
              reportData={reportData}
            />
          </PdfWrapper>
        )}

        {/* Loading */}
        {tableLoading && (
          <div className="w-full">
            <Loader className="float-right" active />
          </div>
        )}

        {/* Report Unavailable Message */}
        {!error && !dropdownLoading && !hasReconciliations && (
          <Alert
            variant="warning"
            title="Unavailable"
            description="User has no financial account reconciliations. These are required for generating a balance sheet."
            className="w-full"
          />
        )}

        {/* Report Error Message */}
        {error && (
          <Alert
            variant="error"
            title="An error occurred"
            description={error}
            className="w-full"
          />
        )}
      </div>
    </div>
  )
}
